import { Button, CircularProgress, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { StyleObj } from '../../@types';
import { ApprovalAction } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { ApprovalActionConfig } from '../../constants/approvalActions';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import useMutateData from '../../hooks/useMutateData';
import ConfirmationModal from '../modals/ConfirmationModal';

const styles: StyleObj = {
  actionButton: {
    borderRadius: 1,
    height: { xs: 28, md: 32 },
    minWidth: { xs: 72, md: 88 },
    typography: { xs: 'caption', md: 'body2' },
    fontWeight: { xs: 500, md: 600 },
  },
};

type WithdrawalActionButtonsProps = {
  transactionId: string;
  /** Decided by `getWithdrawalActions`; this component only renders them. */
  actions: ApprovalActionConfig[];
};

const WithdrawalActionButtons = ({ transactionId, actions }: WithdrawalActionButtonsProps) => {
  const { openModal } = useModal();
  const [pendingAction, setPendingAction] = useState<ApprovalAction | null>(null);

  const { createDataWithPath, isLoading } = useMutateData(
    API_ROUTES.PAYMENT_REQUESTS,
    [QUERY_KEYS.approvals],
    'wallet',
    false,
    undefined,
    { invalidateOnError: true }
  );

  const executeAction = useCallback(
    (action: ApprovalAction, successMessage: string) => {
      const endpoint = `${API_ROUTES.PAYMENT_REQUESTS}/${transactionId}/${action}`;

      setPendingAction(action);
      createDataWithPath(endpoint, {}, undefined, successMessage, () => setPendingAction(null));
    },
    [createDataWithPath, transactionId]
  );

  const handleActionClick = useCallback(
    (config: ApprovalActionConfig) => {
      openModal(
        <ConfirmationModal
          title={config.confirmationTitle}
          actionButtonLabel='Confirm'
          actionButtonClose='Cancel'
          handleConfirm={() => executeAction(config.action, config.successMessage)}
        />
      );
    },
    [openModal, executeAction]
  );

  const isActionLoading = isLoading || pendingAction !== null;

  if (actions.length === 0) {
    return null;
  }

  return (
    <Stack direction='row' spacing={1}>
      {actions.map((config) => (
        <Button
          key={config.action}
          variant='contained'
          color={config.color}
          size='small'
          onClick={() => handleActionClick(config)}
          disabled={isActionLoading}
          sx={styles.actionButton}
        >
          {pendingAction === config.action ? <CircularProgress size={16} color='inherit' /> : config.label}
        </Button>
      ))}
    </Stack>
  );
};

export default WithdrawalActionButtons;
